import { Link } from "react-router-dom";
import {
    Briefcase,
    FileText,
    ClipboardList,
    Users,
    GraduationCap,
    School,
    MessageCircle,
    History,
    Download,
    BarChart3,
    ArrowUpRight,
} from "lucide-react";
import { getSession } from "@/lib/auth";

const CARDS = [
    {
        to: "/bbrgadminacc/job-role",
        title: "Create Job Role",
        description:
            "Define a new role — title, level, department and location.",
        icon: Briefcase,
        testid: "module-card-job-role",
    },
    {
        to: "/bbrgadminacc/job-description",
        title: "Create Job Description",
        description:
            "Write the full public description for a role, ready to publish.",
        icon: FileText,
        testid: "module-card-job-description",
    },
    {
        to: "/bbrgadminacc/form",
        title: "Create Form",
        description:
            "Build the application form candidates fill in for this role.",
        icon: ClipboardList,
        testid: "module-card-form",
    },
    {
        to: "/bbrgadminacc/degrees",
        title: "Manage Degrees",
        description:
            "Single source of truth for the degree dropdown across every form.",
        icon: GraduationCap,
        testid: "module-card-degrees",
    },
    {
        to: "/bbrgadminacc/colleges",
        title: "Manage Colleges",
        description:
            "Master list powering the Register Now college dropdown. Activate or deactivate anytime.",
        icon: School,
        testid: "module-card-colleges",
    },
    {
        to: "/bbrgadminacc/job-role-master",
        title: "Job Role Master",
        description:
            "Roles offered on the Register Now dropdown — add, edit, activate or deactivate.",
        icon: Briefcase,
        testid: "module-card-job-role-master",
    },
    {
        to: "/bbrgadminacc/attendance-report",
        title: "Attendance Report",
        description:
            "Browse filtered attendance in the browser, sort, page through, then export exactly what you see.",
        icon: BarChart3,
        testid: "module-card-attendance-report",
    },
    {
        to: "/bbrgadminacc/candidates",
        title: "Candidate WhatsApp",
        description:
            "Upload candidates, schedule interview WhatsApps and send OTPs automatically.",
        icon: MessageCircle,
        testid: "module-card-candidates",
    },
    {
        to: "/bbrgadminacc/whatsapp-logs",
        title: "WhatsApp Logs",
        description:
            "Every send in one place — status, error, and one-click resend for any candidate.",
        icon: History,
        testid: "module-card-whatsapp-logs",
    },
    {
        to: "/bbrgadminacc/attendance-export",
        title: "Attendance Export",
        description:
            "Filter by attendance / role / date and export the exact columns you need as CSV or Excel.",
        icon: Download,
        testid: "module-card-attendance-export",
    },
    {
        to: "/bbrgadminacc/applicants",
        title: "View Applicants",
        description:
            "See everyone who has applied and move them through your pipeline.",
        icon: Users,
        testid: "module-card-applicants",
    },
];

function greeting(now) {
    const h = now.getHours();
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    return "Good Evening";
}

function formatDate(now) {
    return now.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

function formatDay(now) {
    return now.toLocaleDateString("en-GB", { weekday: "long" });
}

export default function Modules() {
    const now = new Date();
    const session = getSession();
    const name = session?.username
        ? session.username.charAt(0).toUpperCase() + session.username.slice(1)
        : "Admin";

    return (
        <div data-testid="admin-modules-page">
            {/* Greeting hero */}
            <section className="bg-white border-b border-[#0F1F38]/10">
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-8">
                        <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/50 mb-4">
                            Modules
                        </div>
                        <h1
                            data-testid="admin-greeting"
                            className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-[#0F1F38]"
                        >
                            {greeting(now)},{" "}
                            <em className="italic not-italic text-[#0F1F38]/70">
                                {name}.
                            </em>
                        </h1>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <div
                            data-testid="admin-current-day"
                            className="font-display text-2xl md:text-3xl leading-none text-[#0F1F38]"
                        >
                            {formatDay(now)}
                        </div>
                        <div
                            data-testid="admin-current-date"
                            className="mt-2 text-sm text-[#4B5563] tracking-wide"
                        >
                            {formatDate(now)}
                        </div>
                    </div>
                </div>
            </section>

            {/* Module cards */}
            <section
                data-testid="admin-modules-grid"
                className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16"
            >
                <div className="mb-8 flex items-baseline justify-between">
                    <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                        Quick actions
                    </div>
                    <div className="text-xs text-[#4B5563]">
                        {CARDS.length} modules
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                    {CARDS.map(
                        ({ to, title, description, icon: Icon, testid }) => (
                            <Link
                                key={to}
                                to={to}
                                data-testid={testid}
                                className="group relative bg-white border border-[#0F1F38]/10 p-8 flex flex-col gap-6 hover:border-[#0F1F38] transition-colors duration-200"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-11 h-11 flex items-center justify-center bg-[#F7F5F0] border border-[#0F1F38]/10 group-hover:bg-[#0F1F38] group-hover:text-[#F7F5F0] transition-colors duration-200">
                                        <Icon size={18} strokeWidth={1.5} />
                                    </div>
                                    <ArrowUpRight
                                        size={20}
                                        className="text-[#0F1F38]/30 group-hover:text-[#0F1F38] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </div>
                                <div>
                                    <div className="font-display text-2xl md:text-3xl leading-tight text-[#0F1F38]">
                                        {title}
                                    </div>
                                    <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">
                                        {description}
                                    </p>
                                </div>
                            </Link>
                        ),
                    )}
                </div>
            </section>
        </div>
    );
}
